import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOfferNumbers as fetchOfferNumbersApi,
  createOfferNumber as createOfferNumberApi,
  updateOfferNumber as updateOfferNumberApi,
  deleteOfferNumber as deleteOfferNumberApi,
} from "../../services/api";
import { OfferNumber } from "../../models/OfferNumber";

interface OfferNumberState {
  offerNumbers: OfferNumber[];
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

const initialState: OfferNumberState = {
  offerNumbers: [],
  loading: "idle",
  error: null,
};

export const fetchOfferNumbers = createAsyncThunk(
  "offerNumbers/fetchOfferNumbers",
  async () => {
    const response = await fetchOfferNumbersApi();
    return response;
  }
);

export const addOfferNumber = createAsyncThunk(
  "offerNumbers/addOfferNumber",
  async (data: Omit<OfferNumber, "id">) => {
    const response = await createOfferNumberApi(data);
    return response;
  }
);

export const editOfferNumber = createAsyncThunk(
  "offerNumbers/editOfferNumber",
  async ({ id, data }: { id: number; data: Partial<OfferNumber> }) => {
    const response = await updateOfferNumberApi(id, data);
    return response;
  }
);

export const removeOfferNumber = createAsyncThunk(
  "offerNumbers/removeOfferNumber",
  async (id: number) => {
    await deleteOfferNumberApi(id);
    return id;
  }
);

const offerNumberSlice = createSlice({
  name: "offerNumbers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOfferNumbers.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchOfferNumbers.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.offerNumbers = action.payload;
      })
      .addCase(fetchOfferNumbers.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message || "Failed to fetch offer numbers";
      })
      .addCase(addOfferNumber.fulfilled, (state, action) => {
        state.offerNumbers.unshift(action.payload);
      })
      .addCase(editOfferNumber.fulfilled, (state, action) => {
        const index = state.offerNumbers.findIndex(
          (n) => n.id === action.payload.id
        );
        if (index !== -1) {
          state.offerNumbers[index] = action.payload;
        }
      })
      .addCase(removeOfferNumber.fulfilled, (state, action) => {
        state.offerNumbers = state.offerNumbers.filter(
          (n) => n.id !== action.payload
        );
      });
  },
});

export default offerNumberSlice.reducer;
